import * as d3 from 'd3'
import * as dc from 'dc'
import { formatCompactAmount } from '@/utils/utils'

const EPSILON = 0.00001
const INCLUDED_STATUSES = new Set(['REJECTED'])
const IGNORED_KEY = '__IGNORE__'

function normalizeText(value) {
  return String(value || '').trim().toUpperCase()
}

function toNumber(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }

  if (value == null || value === '' || value === '-') {
    return null
  }

  const number = Number(
    String(value)
      .trim()
      .replace(/,/g, '')
  )

  return Number.isFinite(number) ? number : null
}

function normalizeTinyValue(value) {
  const number = Number(value || 0)

  return Math.abs(number) < EPSILON
    ? 0
    : number
}

function createEffectiveSpotMap(rows) {
  const spotByRow = new WeakMap()
  let lastValidSpot = 0

  for (const row of rows) {
    const spot = toNumber(row.spotfx)

    if (spot != null && spot > 0) {
      lastValidSpot = spot
    }

    spotByRow.set(row, lastValidSpot)
  }

  return spotByRow
}

function getBrlNotional(row, effectiveSpot) {
  const notional = toNumber(row.notional)

  if (notional == null) return null
  if (normalizeText(row.currency) === 'BRL') return notional

  return effectiveSpot ? notional * effectiveSpot : null
}

function getRejectionReason(row) {
  return String(
    row.rejectedmessage ??
    row.rejectedMessage ??
    row.rejectionLabel ??
    row.rejectionlabel ??
    row.rejectionReason ??
    row.rejectionreason ??
    row.rejectReason ??
    row.rejectreason ??
    row.reason ??
    ''
  ).trim()
}


function getLegendValue(legendItem) {
  const data = legendItem?.data

  if (typeof data === 'object' && data !== null) {
    return normalizeTinyValue(data.value)
  }

  return normalizeTinyValue(data)
}

export function createRejectionPieChart({
  el,
  legendEl,
  ndx,
  store,
  chartGroup,
  width,
  onTotalChange = () => {},
}) {

  const dimension = store.groupRejectionLabels(ndx)
  const effectiveSpotByRow = createEffectiveSpotMap(ndx.all())

  const rejectionVolumeGroup = dimension.group().reduceSum((row) => {
    const status = normalizeText(row.status)

    if (!INCLUDED_STATUSES.has(status)) {
      return 0
    }

    const amount = getBrlNotional(row, effectiveSpotByRow.get(row))

    if (amount == null) {
      return 0
    }

    return amount
  })


  const getVisibleData = () => {
    return rejectionVolumeGroup
      .all()
      .filter(({ key, value }) => {
        const hasValidKey =
          key !== IGNORED_KEY &&
          String(key || '').trim() !== ''

        const hasValidValue =
          normalizeTinyValue(value) > 0

        return hasValidKey && hasValidValue
      })
  }

  const visibleGroup = {
    all: getVisibleData,
  }

  const getTotal = () => {
  const total = ndx.allFiltered().reduce((sum, row) => {
    if (normalizeText(row.status) !== 'REJECTED') return sum;
    if (!getRejectionReason(row)) return sum;

    const amount = getBrlNotional(row, effectiveSpotByRow.get(row));
    if (amount == null) return sum;

    return sum + amount;
  }, 0);

  return normalizeTinyValue(total);
};

  const chart = new dc.PieChart(el, chartGroup)

  chart
    .width(width)
    .height(200)
    .innerRadius(0)
    .controlsUseVisibility(true)
    .dimension(dimension)
    .group(visibleGroup)
    .drawPaths(true)
    .label(() => '')
    .colors(dc.config.defaultColors())
    .legend(
      new dc.HtmlLegend()
        .container(legendEl)
        .horizontal(false)
        .highlightSelected(true)
        .legendText((item) => {
          const total = getTotal()
          const value = getLegendValue(item)

          if (total <= 0 || value <= 0) {
            return ''
          }

          const percentage = (value / total) * 100

          const formattedPercentage =
            percentage < 0.1
              ? percentage.toFixed(5)
              : percentage.toFixed(1)

          const formattedValue = formatCompactAmount(value)

          const name = String(item.name || '')
          const title =
            name.charAt(0).toUpperCase() +
            name.slice(1)

          return `${title} (${formattedPercentage}%, ${formattedValue})`
        })
    )

  const updateTotal = () => {
    const total = getTotal()
    const hasData = total > 0

    const svg = d3.select(el).select('svg')
    const legend = d3.select(legendEl)

    /*
     * Hide dc.js's empty placeholder circle instead of painting it white.
     */
    svg
      .selectAll('g.pie-slice-group')
      .style('display',null)
      .style('fill', hasData ? null : 'white');
      
      if (!hasData) {
      legend.html('')
      onTotalChange(0)
      return
    }

    onTotalChange(formatCompactAmount(total))
  }

  chart.on('postRender.totalUpdate', updateTotal)
  chart.on('postRedraw.totalUpdate', updateTotal)
  chart.on('filtered.totalUpdate', updateTotal)

  chart.render()
  updateTotal()

  return chart
}
